import { colors } from 'global/variables';
import {
  createOrderClusterMarkerDoubleSvg,
  createOrderClusterMarkerMultipleSvg,
  createStopClusterMarkerDoubleSvg,
  createStopClusterMarkerMultipleSvg,
  createParkingClusterMarkerDoubleSvg,
  createParkingClusterMarkerMultipleSvg,
  getSpecialStopClusterIcon,
} from '../../../../icons/marker';

const SPECIAL_STOP_TYPES = [
  'break',
  'reload',
  'recharge',
  'pudo',
  'drivingRestTime',
  'workingRestTime',
  'dutyWorkingRestTime',
  'dutyDrivingRestTime',
];

const MIXED_TYPES_COLOR = { inner: colors.grey, outer: colors.grey, text: colors.white };

export const STOP_CLUSTER_ANCHOR = { x: 20, y: 23 };
export const ACTIVITY_CLUSTER_ANCHOR = { x: 7, y: 10 };

function resolveStopClusterMeta(dataPoints) {
  const allTypes = dataPoints.flatMap((p) => p.types || []);
  const uniqueTypes = [...new Set(allTypes)];
  const isParking = dataPoints.every((p) => p.types?.includes('parking'));
  const isUniformSpecialType =
    !isParking && uniqueTypes.length === 1 && SPECIAL_STOP_TYPES.includes(uniqueTypes[0]);
  const isMixedSpecial =
    !isParking && !isUniformSpecialType && uniqueTypes.some((t) => SPECIAL_STOP_TYPES.includes(t));
  const jobType = isUniformSpecialType ? uniqueTypes[0] : dataPoints[0]?.types?.[0];
  const jobMarkerColor = dataPoints[0]?.jobMarkerColor;
  const colorToUse = isMixedSpecial
    ? MIXED_TYPES_COLOR
    : jobType === 'pickup'
    ? jobMarkerColor?.pickup
    : jobMarkerColor?.delivery;

  return { colorToUse, isParking, isUniformSpecialType, isMixedSpecial, uniqueTypes };
}

export function isClusterHighlighted(dataPoints, highlightStop) {
  if (!highlightStop?.id || highlightStop.id === -1) return false;
  return dataPoints.some(
    (p) =>
      highlightStop.tourId === p.routeId &&
      ((p.jobOrder !== undefined && p.jobOrder === highlightStop.id) ||
        (p.stopIndex !== undefined && p.stopIndex === highlightStop.id)),
  );
}

export function buildStopClusterIcon(dataPoints, highlightStop) {
  const count = dataPoints.length;
  const { colorToUse, isParking, isUniformSpecialType, uniqueTypes } =
    resolveStopClusterMeta(dataPoints);
  const specialIcon = isUniformSpecialType ? getSpecialStopClusterIcon(uniqueTypes[0]) : '';
  const highlighted = isClusterHighlighted(dataPoints, highlightStop);
  const highPriority = dataPoints.some((p) => p.highPriority);
  const sorted = [...dataPoints].sort((a, b) => {
    const aOrder = a.jobOrder ?? a.stopIndex ?? Infinity;
    const bOrder = b.jobOrder ?? b.stopIndex ?? Infinity;
    return aOrder - bOrder;
  });
  const displayInfo = sorted[0]?.jobOrder ?? sorted[0]?.stopIndex ?? count;
  const strokeColor = highlighted ? colors.yellow : highPriority ? colors.orange : colorToUse?.outer;

  if (isParking) {
    return count === 2
      ? createParkingClusterMarkerDoubleSvg(40, 46)
      : createParkingClusterMarkerMultipleSvg(40, 46);
  }

  if (count === 2) {
    return createStopClusterMarkerDoubleSvg(
      colorToUse?.inner,
      strokeColor,
      40,
      46,
      specialIcon ? '' : String(displayInfo),
      colorToUse?.text,
      specialIcon,
    );
  }

  return createStopClusterMarkerMultipleSvg(
    colorToUse?.inner,
    strokeColor,
    40,
    46,
    specialIcon ? '' : String(displayInfo),
    colorToUse?.text,
    specialIcon,
  );
}

export function buildActivityClusterIcon(dataPoints, highlightStop) {
  const { colorToUse, isUniformSpecialType, uniqueTypes } = resolveStopClusterMeta(dataPoints);
  const specialIcon = isUniformSpecialType ? getSpecialStopClusterIcon(uniqueTypes[0]) : '';
  const highlighted = isClusterHighlighted(dataPoints, highlightStop);
  const highPriority = dataPoints.some((p) => p.highPriority);
  const strokeColor = highlighted ? colors.yellow : highPriority ? colors.orange : colorToUse?.outer;

  return createStopClusterMarkerDoubleSvg(
    colorToUse?.inner,
    strokeColor,
    14,
    20,
    '',
    colorToUse?.text,
    specialIcon,
  );
}

export function buildOrderClusterIcon(dataPoints) {
  const count = dataPoints.length;
  const highPriority = dataPoints.some((p) => p.highPriority);
  const strokeColor = highPriority ? colors.orange : undefined;

  return count === 2
    ? createOrderClusterMarkerDoubleSvg(40, 46, count, strokeColor)
    : createOrderClusterMarkerMultipleSvg(40, 46, count, strokeColor);
}

